import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, ArrowRight } from "lucide-react";

import { authApi, setToken } from "@/lib/api";
import { ASSETS } from "@/lib/company";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("owner@mingaarchitecture.com");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      const res = await authApi.login(email, password);
      setToken(res.token);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-black text-brand-white dark grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col justify-between p-8 md:p-14">
        <div>
          <Link to="/" className="inline-flex items-center" aria-label="Minga Architecture">
            <img src={ASSETS.logo} alt="Minga Architecture" className="h-40 w-auto object-contain"/>
          </Link>
        </div>

        <div className="max-w-md w-full mx-auto">
          <p className="eyebrow">[ Admin Access ]</p>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mt-4">
            Welcome back.
          </h1>
          <p className="body-text mt-3">
            Sign in to manage content, projects and client enquiries.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="admin-login-form">
            <label className="block">
              <span className="eyebrow-muted block mb-2">Email</span>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-login-email"
                  className="w-full bg-white/[0.03] border border-white/12 rounded-sm pl-10 pr-4 py-3 text-sm outline-none focus:border-brand-yellow transition-colors font-body"
                  placeholder="you@mingaarchitecture.com"
                />
              </div>
            </label>

            <label className="block">
              <span className="eyebrow-muted block mb-2">Password</span>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray" />
                <input
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-login-password"
                  className="w-full bg-white/[0.03] border border-white/12 rounded-sm pl-10 pr-11 py-3 text-sm outline-none focus:border-brand-yellow transition-colors font-body"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center text-brand-gray hover:text-brand-yellow"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>

            {error && (
              <div
                className="border-l-2 border-brand-red pl-4 py-2 bg-brand-red/5 text-sm"
                data-testid="admin-login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {loading ? "Signing in\u2026" : "Sign In"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>

        <div className="flex items-center justify-between text-xs text-brand-gray mt-10">
          <Link to="/" className="link-underline">
            ← Back to Minga Architecture
          </Link>
          <span className="uppercase tracking-[0.2em]">Command Center</span>
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden lg:block relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(/assets/minga/projects/waters-residence/02-dining.jpg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-ink-black/85 via-ink-black/60 to-ink-black/85" />
        <div className="absolute inset-0 blueprint opacity-40" />
        <div className="relative z-10 h-full p-14 flex flex-col justify-end">
          <p className="eyebrow">[ Command Center ]</p>
          <h2 className="section-headline mt-4 max-w-md">
            The studio behind the studio.
          </h2>
          <p className="body-text mt-6 max-w-md">
            Manage the Minga Architecture website, projects, media, testimonials and
            client enquiries from a single elegant workspace.
          </p>
        </div>
      </div>
    </div>
  );
}
